import {
  Article as ArticleIcon,
  ChatBubbleOutline as CommentIcon,
  DynamicFeed as FeedIcon,
  Favorite as FavoriteIcon,
  Login as LoginIcon,
  Newspaper as NewspaperIcon,
  OpenInNew as OpenInNewIcon,
  ShoppingBag as ShoppingBagIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  Container,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import { Post, postApi } from '../services/postApi';
import { colors } from '../theme';

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'news': return <NewspaperIcon />;
    case 'product': return <ShoppingBagIcon />;
    default: return <ArticleIcon />;
  }
};

const getTypeLabel = (type: string) => {
  switch (type) {
    case 'news': return 'Haber';
    case 'product': return 'Ürün';
    default: return 'Yazı';
  }
};

const formatTimeAgo = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Bugün';
  if (diffDays === 1) return 'Dün';
  if (diffDays < 7) return `${diffDays} gün önce`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} hafta önce`;
  return date.toLocaleDateString('tr-TR');
};

const PublicFeedPage: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await postApi.getTopPublicPosts(20);
        setPosts(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleViewDetail = (postId: string) => {
    navigate(`/feed/${postId}`);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: colors.background?.main || '#f5f7fa' }}>
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid #eee',
          py: 2,
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <FeedIcon sx={{ color: colors.primary.main, fontSize: 32 }} />
              <Typography variant="h5" fontWeight={700} color="text.primary">
                Paylaşımlar
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
                sx={{
                  textTransform: 'none',
                  borderColor: colors.primary.main,
                  color: colors.primary.main,
                  '&:hover': {
                    borderColor: colors.primary.dark,
                    bgcolor: colors.primary.main,
                    color: 'white',
                  },
                }}
              >
                Ana Sayfa
              </Button>
              <Button
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={() => navigate('/client/login')}
                sx={{
                  bgcolor: colors.primary.main,
                  textTransform: 'none',
                  '&:hover': { bgcolor: colors.primary.dark },
                  width: '300px',
                }}
              >
                Giriş Yap
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, py: 2, px: 0 }}>
        {loading ? (
          <Loading message="Paylaşımlar yükleniyor..." />
        ) : posts.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography color="text.secondary">Henüz herkese açık paylaşım yok.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {posts.map((post) => (
              <Card
                key={post.id}
                elevation={0}
                sx={{
                  border: '1px solid #efefef',
                  borderRadius: 0,
                  bgcolor: 'white',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 1.5,
                    borderBottom: '1px solid #efefef',
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: colors.primary.main,
                        fontSize: '0.875rem',
                      }}
                    >
                      {(post.dietitianName || 'D').charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#262626' }}>
                      {post.dietitianName}
                    </Typography>
                  </Box>
                  <Chip
                    icon={getTypeIcon(post.type)}
                    label={getTypeLabel(post.type)}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      bgcolor: colors.alpha?.primary10 || '#e8f5e9',
                      color: colors.primary.main,
                    }}
                  />
                </Box>

                {post.imageUrl && (
                  <CardMedia
                    component="img"
                    image={post.imageUrl}
                    alt={post.title}
                    onClick={() => handleViewDetail(post.id)}
                    sx={{
                      width: '100%',
                      aspectRatio: '1 / 1',
                      objectFit: 'cover',
                      cursor: 'pointer',
                    }}
                  />
                )}

                <Box sx={{ p: 1.5 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <IconButton size="small" sx={{ color: '#262626', width: 'fit-content' }}>
                      <FavoriteIcon sx={{ fontSize: 24, width: 'fit-content' }} />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#262626', width: 'fit-content' }}>
                      <CommentIcon fontSize="medium" sx={{ width: 'fit-content' }} />
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton
                      size="small"
                      onClick={() => handleViewDetail(post.id)}
                      sx={{ color: '#262626', width: 'fit-content' }}
                    >
                      <OpenInNewIcon fontSize="small" sx={{ width: 'fit-content' }} />
                    </IconButton>
                  </Box>

                  {post.likeCount != null && post.likeCount > 0 && (
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#262626' }}>
                      {post.likeCount} beğeni
                    </Typography>
                  )}

                  <Box sx={{ mb: 0.5 }}>
                    <Typography variant="body2" component="span" fontWeight={600} sx={{ color: '#262626', mr: 1 }}>
                      {post.dietitianName}
                    </Typography>
                    <Typography variant="body2" component="span" sx={{ color: '#262626' }}>
                      {post.content.length > 120 ? `${post.content.slice(0, 120)}...` : post.content}
                    </Typography>
                  </Box>

                  {post.tags && post.tags.length > 0 && (
                    <Box sx={{ mb: 0.5 }}>
                      {post.tags.slice(0, 5).map((tag) => (
                        <Typography
                          key={tag}
                          variant="body2"
                          component="span"
                          sx={{ color: '#00376b', mr: 1 }}
                        >
                          #{tag}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  <Button
                    size="small"
                    onClick={() => handleViewDetail(post.id)}
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14, width: 'fit-content' }} />}
                    sx={{
                      textTransform: 'none',
                      color: '#8e8e8e',
                      p: 0,
                      minWidth: 'auto',
                      '&:hover': { bgcolor: 'transparent', color: colors.primary.main },
                    }}
                  >
                    Detayı gör
                  </Button>

                  <Typography variant="caption" display="block" sx={{ color: '#8e8e8e', mt: 0.5 }}>
                    {formatTimeAgo(post.createdAt)}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </Container>
      <Footer />
    </div>
  );
};

export default PublicFeedPage;
